import { useEffect, useState } from "react";

/**
 * Input numérico que SIEMPRE muestra el punto como separador decimal.
 *
 * Un <input type="number"> dibuja el valor con el separador del idioma del
 * navegador: en un Chrome en español el tipo de cambio 507.43 aparecía como
 * "507,43", distinto al formato del resto del sistema (1,234.56). Este input
 * es de texto, así que se ve igual en cualquier navegador; al escribir acepta
 * tanto "," como "." y los deja como punto.
 *
 * Es un reemplazo directo: `onChange` recibe un objeto con `target.value` (el
 * texto ya normalizado, p. ej. "507.43"), igual que el evento de un input
 * nativo, así que los handlers existentes (`e => parseFloat(e.target.value)`)
 * siguen funcionando sin cambios.
 *
 * Mientras el campo tiene foco se respeta lo que el usuario escribe (se puede
 * borrar todo o dejar "507." a medias); al salir se vuelve a mostrar el valor
 * que quedó en el estado del padre.
 */
function aTexto(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return String(value);
}

export function normalizarDecimal(raw, { decimals = 4, allowNegative = false } = {}) {
  let s = String(raw ?? "").replace(/,/g, ".").replace(/[^\d.-]/g, "");
  const negativo = allowNegative && s.startsWith("-");
  s = s.replace(/-/g, "");
  const punto = s.indexOf(".");
  if (punto !== -1) {
    const entero = s.slice(0, punto);
    const dec = s.slice(punto + 1).replace(/\./g, "").slice(0, decimals);
    s = `${entero}.${dec}`;
  }
  return (negativo ? "-" : "") + s;
}

export default function DecimalInput({
  value,
  onChange,
  decimals = 4,
  allowNegative = false,
  onFocus,
  onBlur,
  // Props de <input type="number"> que no aplican a un input de texto.
  step, min, max, // eslint-disable-line no-unused-vars
  ...rest
}) {
  const [draft, setDraft] = useState(aTexto(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(aTexto(value));
  }, [value, focused]);

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={draft}
      onFocus={(e) => { setFocused(true); onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); onBlur?.(e); }}
      onChange={(e) => {
        const limpio = normalizarDecimal(e.target.value, { decimals, allowNegative });
        setDraft(limpio);
        onChange?.({ target: { value: limpio, name: rest.name } });
      }}
    />
  );
}
